import React from 'react';
import PropTypes from 'prop-types';
import * as RadixDialog from '@radix-ui/react-dialog';

// Utils
import bemify from 'utils/bemify';

// Components
import Icon from 'components/icon';

// Styles
import stylesModule from './styles.module.scss';


const styles = bemify(stylesModule);

const Modal = ({
  ariaTitle = null,
  children = null,
  className = null,
  customHeader = null,
  description = null,
  descriptionTextColor = 'gray',
  footer = null,
  footerClassName = null,
  footerLeftSlot = null,
  headerClassName = null,
  headerIcon = null,
  onClose = () => {},
  mobilePosition = 'center',
  noCloseIcon = false,
  show = false,
  size = 'default',
  title = null,
  trigger = null,
  triggerClassName = null
}) => {
  if (!customHeader && !description && !title && !children) return null;

  const handleModalOpenChange = (value) => {
    if (value === false) onClose();
  };

  const showsVisibleTitle = !customHeader && !!title;
  const showsVisibleDescription = !customHeader && !!description;
  const accessibleTitle = title || ariaTitle || '';

  const contentWrapperClassNames = `${styles.modal__contentWrapper}${
    className ? ` ${className}` : ''}${
    mobilePosition === 'bottom' ? ` ${styles.modal__contentWrapper_bottom}` : ''}${
    mobilePosition === 'top' ? ` ${styles.modal__contentWrapper_top}` : ''}${
    size === 'l' ? ` ${styles.modal__contentWrapper_large}` : ''}${
    size === 's' ? ` ${styles.modal__contentWrapper_small}` : ''}${
    size === 'xl' ? ` ${styles.modal__contentWrapper_extraLarge}` : ''
  }`;

  return (
    <RadixDialog.Root
      open={show}
      onOpenChange={handleModalOpenChange}
    >
      <RadixDialog.Trigger
        asChild
        className={triggerClassName || null}
      >
        {trigger}
      </RadixDialog.Trigger>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className={styles.modal__overlay} />
        <RadixDialog.Content className={contentWrapperClassNames}>
          {!showsVisibleTitle && (
            <RadixDialog.Title asChild>
              <span className={styles.modal__visuallyHidden}>
                {accessibleTitle}
              </span>
            </RadixDialog.Title>
          )}
          {!showsVisibleDescription && (
            <RadixDialog.Description className={styles.modal__visuallyHidden} />
          )}
          {(!!customHeader || !!title || description || !!headerIcon || !noCloseIcon) && (
            <header className={`${styles.modal__contentHeader}${
              headerClassName ? ` ${headerClassName}` : ''}`}
            >
              {!!headerIcon && (
                <div className={styles.modal__contentHeaderIconWrapper}>
                  <Icon
                    className={styles.modal__contentHeaderIcon}
                    name={headerIcon}
                  />
                </div>
              )}
              {customHeader}
              {!customHeader && (!!title || !!description) && (
                <div className={!noCloseIcon ? styles.modal__contentHeader_hasCloseIcon : null}>
                  {!!title && (
                    <RadixDialog.Title asChild>
                      <div className={styles.modal__title}>
                        {title}
                      </div>
                    </RadixDialog.Title>
                  )}
                  {!!description && (
                    <RadixDialog.Description asChild>
                      <div className={`${
                        styles.modal__description}${
                        descriptionTextColor === 'dark-blue' ? ` ${styles.modal__description_darkBlue}` : ''}`}
                      >
                        {description}
                      </div>
                    </RadixDialog.Description>
                  )}
                </div>
              )}
              {!noCloseIcon && (
                <RadixDialog.Close className={styles.modal__closeButton}>
                  <Icon name="close" />
                </RadixDialog.Close>
              )}
            </header>
          )}
          {!!children && (
            <div className={styles.modal__contentChildren}>
              {children}
            </div>
          )}
          {footer && (
            <div className={`${styles.modal__contentFooter}${
              footerLeftSlot ? ` ${styles.modal__contentFooter_hasLeftSlot}` : ''}${
              footerClassName ? ` ${footerClassName}` : ''}`}
            >
              {!!footerLeftSlot && footerLeftSlot}
              <div>
                {footer}
              </div>
            </div>
          )}
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
};

Modal.propTypes = {
  // The dialog must have an accessible name while it is open. When a customHeader
  // replaces the built-in title, callers must supply title or ariaTitle for screen
  // readers. Only enforced when shown, since Radix renders nothing while closed.
  ariaTitle: (props, propName, componentName) => {
    if (props.show && props.customHeader && !props.title && !props.ariaTitle) {
      return new Error(`${componentName}: \`title\` or \`ariaTitle\` is required when \`customHeader\` is set, to provide an accessible name.`);
    }

    if (props[propName] != null && typeof props[propName] !== 'string') {
      return new Error(`${componentName}: \`${propName}\` must be a string.`);
    }

    return null;
  },
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]),
  className: PropTypes.string,
  customHeader: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.arrayOf(PropTypes.element)]),
  description: PropTypes.string,
  descriptionTextColor: PropTypes.oneOf(['dark-blue', 'gray']),
  footer: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.arrayOf(PropTypes.element)]),
  footerClassName: PropTypes.string,
  footerLeftSlot: PropTypes.element,
  headerClassName: PropTypes.string,
  headerIcon: PropTypes.string,
  mobilePosition: PropTypes.oneOf(['top', 'center', 'bottom']),
  noCloseIcon: PropTypes.bool,
  onClose: PropTypes.func,
  show: PropTypes.bool,
  size: PropTypes.oneOf(['default', 's', 'l', 'xl']),
  title: PropTypes.string,
  trigger: PropTypes.element,
  triggerClassName: PropTypes.string
};

export default Modal;
